import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FilterX, SlidersHorizontal, X } from 'lucide-react';
import { ehDataIso, ehUuidAceito } from '@malgoos/shared';
import { get } from '../nucleo/api';
import { dinheiro, nomePessoa } from '../nucleo/formato';
import { useSessao } from '../nucleo/sessao';
import { Botao, Segmentado } from './base';
import { BuscaRemota, EntradaDinheiro, Selecao } from './formulario';
import { Popover } from './menu';
import { SeletorPeriodo } from './periodo/SeletorPeriodo';
import { escolhaDoPeriodo, hojeNoFuso, periodoDoAtalho, periodoValido, type Periodo, type PeriodoGuardado } from './periodo/periodo';
import { useDiscreto } from './sensivel';
import { useDebounce } from './tabela';

/**
 * Fase 2 (W36): controles dos filtros das listas, montados com os componentes que já existiam (Selecao, `SeletorPeriodo`,
 * `BuscaRemota` com os chips do Folha > Benefícios, o campo de dinheiro do formulário). Ficam na seção "Filtrar" da tela,
 * à vista (nada de "Mais filtros" num popover), e cada tela guarda os valores na URL e na visão salva. As regras puras
 * ficam exportadas para teste.
 */

/** Teto de clientes ou fornecedores no filtro (o mesmo da API, `MAX_IDS_FILTRO`). */
export const MAX_PARCEIROS_FILTRO = 20;

/** Lista de ids da URL (`a,b,c`): sem vazios nem repetidos, no máximo o teto. */
export function idsDaUrl(v: string | null | undefined): string[] {
  return [...new Set((v ?? '').split(',').map((x) => x.trim()).filter(Boolean))].slice(0, MAX_PARCEIROS_FILTRO);
}

// Review r28 robustez (B2): o mesmo teste de uuid da entrada da API (`z.uuid()` e sem os reservados), `ehUuidAceito`.

/**
 * Review r26 robustez (O6): valor da URL que a API recusaria (`?tipo=xx`, `?origem=x`, id que não é uuid) é ignorado, em
 * vez de a lista inteira cair em "Indisponível" (422). Lista: só os aceitos; valor único: o aceito ou vazio.
 */
export const aceitosDaUrl = (v: string | null | undefined, aceitos: readonly string[]): string[] => idsDaUrl(v).filter((x) => aceitos.includes(x));
export const aceitoDaUrl = (v: string | null | undefined, aceitos: readonly string[]): string => (v && aceitos.includes(v) ? v : '');
export const uuidsDaUrl = (v: string | null | undefined): string[] => idsDaUrl(v).filter(ehUuidAceito);
export const uuidDaUrl = (v: string | null | undefined): string => (v && ehUuidAceito(v.trim()) ? v.trim() : '');
/** Review r27 spec (B1): data da URL (`de`, `ate`) só quando a API aceita (AAAA-MM-DD existente); senão, vazio. */
export const dataDaUrl = (v: string | null | undefined): string => (v && ehDataIso(v) ? v : '');
/**
 * Review r28 spec (B1): o par de datas da URL só vale junto, quando é o período que o `FiltroPeriodo` mostra (as duas datas
 * existentes, "de" até "ate", no máximo 3 anos); senão, nenhuma das duas. A tela e a API usam o mesmo par: antes, com
 * `?de=xx&ate=2026-02-30` a tela mostrava a base e o mês atual e a API listava tudo, e com `?de=2026-10-01&ate=xx` a API
 * filtrava de 01/10 sem fim.
 */
export function periodoDaUrl(de: string | null | undefined, ate: string | null | undefined): { de: string; ate: string } {
  const p = { de: dataDaUrl(de), ate: dataDaUrl(ate) };
  return periodoValido(p) ? p : { de: '', ate: '' };
}
/**
 * Review r27 spec (B1): faixa de valor da URL em centavos, como a API aceita: inteiro não negativo e mínimo até o máximo.
 * Valor inválido sai; faixa invertida sai inteira (a API daria 422 `valor.faixa_invalida` e a lista cairia).
 */
export function faixaDaUrl(min: string | null | undefined, max: string | null | undefined): { valor_min_centavos?: string; valor_max_centavos?: string } {
  const a = centavosDaUrl(min);
  const b = centavosDaUrl(max);
  if (!faixaValida(a, b)) return {};
  return { valor_min_centavos: a === null ? undefined : String(a), valor_max_centavos: b === null ? undefined : String(b) };
}

export type BasePeriodo = 'vencimento' | 'competencia' | 'caixa';

/**
 * Base vigente do período: a da URL (`campo_data`) quando é uma das aceitas; senão, com datas na URL (links antigos, como
 * `?de=&ate=` vindo de um relatório), a primeira base (a padrão da API); sem datas, "todo o período" ('').
 */
export function baseDoPeriodo(bases: readonly BasePeriodo[], campo: string, de: string, ate: string): BasePeriodo | '' {
  if ((bases as readonly string[]).includes(campo) && (de || ate)) return campo as BasePeriodo;
  return de || ate ? bases[0]! : '';
}

/** Valores da URL ao trocar a base: '' limpa tudo; a primeira base não vai para a URL (é a padrão da API). */
export function urlDaBase(bases: readonly BasePeriodo[], base: BasePeriodo | '', atual: Periodo | null, hoje: string): { campo_data: string; de: string; ate: string } {
  if (!base) return { campo_data: '', de: '', ate: '' };
  const p = atual && periodoValido(atual) ? atual : periodoDoAtalho('mes', hoje);
  return { campo_data: base === bases[0] ? '' : base, de: p.de, ate: p.ate };
}

/**
 * "Período por" (Selecao com "Todo o período" e as bases da tela) e, com uma base escolhida, o `SeletorPeriodo` do núcleo.
 * Escolher a base sem datas na URL começa pelo mês atual (fuso do workspace).
 */
export function FiltroPeriodo({ bases, campo, de: deUrl, ate: ateUrl, aoMudar }: { bases: readonly BasePeriodo[]; campo: string; de: string; ate: string; aoMudar: (v: { campo_data: string; de: string; ate: string }) => void }) {
  const { t } = useTranslation();
  const s = useSessao();
  const hoje = hojeNoFuso(s.workspace.fuso);
  // Review r28 spec (B1): o período à vista é o mesmo que vai à API (`periodoDaUrl`); par inválido é "Todo o período".
  const { de, ate } = periodoDaUrl(deUrl, ateUrl);
  const base = baseDoPeriodo(bases, campo, de, ate);
  const atual = periodoValido({ de, ate }) ? { de, ate } : null;
  const periodo = atual ?? periodoDoAtalho('mes', hoje);
  const id = useId();
  return (
    <>
      {/* Review r25 visual (B4): "Período por" com rótulo visível (o pequeno do "Valor mínimo"), não só no aria-label. */}
      <div className="col-span-2 flex min-w-0 flex-col gap-1 sm:col-span-1 sm:w-48">
        <label htmlFor={id} className="text-xs font-medium text-tinta-3">
          {t('filtrosLista.periodoPor')}
        </label>
        <Selecao
          id={id}
          vazio={t('filtrosLista.todoPeriodo')}
          opcoes={bases.map((b) => ({ valor: b, rotulo: t(`filtrosLista.bases.${b}`) }))}
          value={base}
          onChange={(e) => aoMudar(urlDaBase(bases, e.target.value as BasePeriodo | '', atual, hoje))}
          data-filtro-base=""
        />
      </div>
      {base && (
        // A vaga tem quase a largura do painel do seletor, que abre alinhado à direita dela (W33: 320 px).
        <div className="col-span-2 sm:col-span-1 sm:w-72" data-filtro-periodo="">
          <SeletorPeriodo
            periodo={periodo}
            escolha={escolhaDoPeriodo(periodo, hoje)}
            aoEscolher={(e: PeriodoGuardado) => {
              const p = e.atalho === 'personalizado' ? { de: e.de, ate: e.ate } : periodoDoAtalho(e.atalho, hoje);
              aoMudar({ campo_data: base === bases[0] ? '' : base, de: p.de, ate: p.ate });
            }}
          />
        </div>
      )}
    </>
  );
}

export type PapelFiltro = 'cliente' | 'fornecedor' | 'todos';

/**
 * Clientes ou fornecedores no filtro: a busca remota do núcleo e, embaixo, um chip por escolhido com o botão de tirar (o
 * mesmo chip do Folha > Benefícios). O nome passa por `nomePessoa`: com a opção de nomes do modo discreto, sai em iniciais
 * no chip e no rótulo do botão. Sem `cadastros.ver`, não há busca nem nome (o chip diz que o cadastro não está acessível).
 * crm8 (2): a busca é a da operação (`operacao`), sem o lead que só está no CRM; o nome dos já escolhidos vem pelos ids, sem
 * esse filtro.
 */
export function FiltroParceiros({ papel, ids, aoMudar, parte }: { papel: PapelFiltro; ids: string[]; aoMudar: (ids: string[]) => void; parte?: ParteFiltro }) {
  const { t } = useTranslation();
  useDiscreto();
  const s = useSessao();
  const pode = s.instalado('cadastros') && s.pode('cadastros.ver');
  const id = useId();
  const [rodada, setRodada] = useState(0);
  const [conhecidos, setConhecidos] = useState<Record<string, string>>({});
  // Nomes dos que vieram pela URL ou pela visão (um pedido só, pelos ids; arquivados também, para o chip não ficar sem nome).
  const faltam = useMemo(() => ids.filter((x) => !conhecidos[x]), [ids, conhecidos]);
  const nomes = useQuery({
    queryKey: ['/cadastros/parceiros', 'nomes-filtro', faltam],
    queryFn: () => get<{ itens: { id: string; nome: string }[] }>('/cadastros/parceiros', { ids: faltam.join(','), arquivados: true, limite: faltam.length }),
    enabled: pode && faltam.length > 0,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!nomes.data?.itens.length) return;
    setConhecidos((c) => ({ ...c, ...Object.fromEntries(nomes.data.itens.map((i) => [i.id, i.nome])) }));
  }, [nomes.data]);
  // Review r29 spec (O1): o desconhecido diz o campo, como categoria e centro ("Cliente: Cadastro sem acesso").
  const semAcessoDoCampo = t('filtrosLista.semAcessoCampo', { campo: t(`filtrosLista.campo.${papel}`) });
  const nomeDe = (x: string) => (conhecidos[x] ? nomePessoa(conhecidos[x]) : semAcessoDoCampo);
  const cheio = ids.length >= MAX_PARCEIROS_FILTRO;
  const classeCampo = useClasseCampo('sm:w-64');

  return (
    <>
      {pode && !cheio && parte !== 'chips' && (
        <div className={classeCampo} data-filtro-parceiros="">
          <label htmlFor={id} className="sr-only">
            {t(`filtrosLista.buscar.${papel}`)}
          </label>
          <BuscaRemota
            key={rodada}
            id={id}
            placeholder={t(`filtrosLista.buscar.${papel}`)}
            def={{ caminho: '/cadastros/parceiros', params: papel === 'todos' ? { operacao: true } : { papel, operacao: true }, rotulo: (i) => String(i.nome) }}
            valor={null}
            aoMudar={(v, item) => {
              if (!v) return;
              if (item) setConhecidos((c) => ({ ...c, [v]: String(item.nome) }));
              if (!ids.includes(v)) aoMudar([...ids, v]);
              setRodada((r) => r + 1);
              requestAnimationFrame(() => document.getElementById(id)?.focus());
            }}
          />
        </div>
      )}
      {parte !== 'campo' && <ChipsFiltro ids={ids} nomeDe={nomeDe} rotulo={t(`filtrosLista.escolhidos.${papel}`)} idCampo={id} cheio={cheio} emLinha={parte === 'chips'} aoMudar={aoMudar} data-chips-parceiros="" />}
    </>
  );
}

/**
 * C1 ("Mais filtros" em painel, como no antigo): o campo do filtro fica no painel (`parte="campo"`) e os chips na linha de
 * chips embaixo da faixa (`parte="chips"`). Sem `parte`, os dois juntos (Compras e Notas de crédito, como antes).
 */
export type ParteFiltro = 'campo' | 'chips';

/** Elemento que pode receber o foco (o bastante do DOM para a regra pura de `alvoAposTirarChip`). */
export interface Focavel {
  focus: () => void;
  disabled?: boolean;
}

/** Review r28 visual (B1): marcas no DOM dos alvos do foco depois de tirar um chip. */
export const SELETOR_TIRAR_CHIP = '[data-tirar-chip]';
export const SELETOR_LIMPAR_FILTROS = '[data-limpar-filtros]';
export const SELETOR_BUSCA_TABELA = '[data-busca-tabela]';

/**
 * Review r29 visual (M3): "Limpar filtros" se desabilita com o foco nele (sem filtro não há o que limpar) e o navegador tira o
 * foco de botão desabilitado, que caía no body. Depois de limpar, o foco vai para a busca da Tabela, a mesma da r28 B1.
 */
export function limparFiltrosEFocarBusca(limpar: () => void, busca: () => Focavel | null = () => document.querySelector<HTMLElement>(SELETOR_BUSCA_TABELA)) {
  limpar();
  busca()?.focus();
}

/**
 * Review r28 visual (B1): para onde vai o foco depois de tirar um chip, na ordem:
 *  1. o chip que ficou no lugar na mesma lista (ou o anterior);
 *  2. o campo do filtro, quando está montado;
 *  3. o chip vizinho de outro filtro do mesmo grupo (o que ficou na posição do tirado, ou o último antes dele);
 *  4. o "Limpar filtros" do grupo, quando habilitado;
 *  5. a busca da Tabela.
 * Antes, sem chip na lista e sem o campo (quem não tem `financeiro.ver` ou `cadastros.ver` e vê "Cadastro sem acesso"), o
 * alvo era o id de uma `Selecao` que não estava montada, e o foco caía no body.
 */
export function alvoAposTirarChip(o: { daLista: readonly Focavel[]; i: number; campo: Focavel | null; doGrupo: readonly Focavel[]; posicaoNoGrupo: number; limpar: Focavel | null; busca: Focavel | null }): Focavel | null {
  if (o.daLista.length) return o.daLista[Math.min(o.i, o.daLista.length - 1)]!;
  if (o.campo) return o.campo;
  if (o.doGrupo.length) return o.doGrupo[Math.min(Math.max(o.posicaoNoGrupo, 0), o.doGrupo.length - 1)]!;
  if (o.limpar && !o.limpar.disabled) return o.limpar;
  return o.busca;
}

/**
 * Chips dos escolhidos num filtro de vários (o chip do Folha > Benefícios, o mesmo da W36): nome com o botão de tirar.
 * Depois de tirar um chip, o foco vai para o chip que ficou no lugar (ou o anterior); sem chips, para o campo do filtro e,
 * sem o campo, para o vizinho no grupo de filtros (`alvoAposTirarChip`).
 * Espera a lista nova (a URL muda de forma assíncrona): focar antes caía no botão que ia sumir, e o foco ia parar no body.
 */
export function ChipsFiltro({ ids, nomeDe, rotulo, idCampo, idLista, cheio, emLinha = false, aoMudar, ...dados }: { ids: string[]; nomeDe: (id: string) => string; rotulo: string; idCampo: string; idLista?: string; cheio: boolean; /** C1: na linha de chips (sem ocupar a linha inteira). */ emLinha?: boolean; aoMudar: (ids: string[]) => void; [dado: `data-${string}`]: string }) {
  const { t } = useTranslation();
  const lista = useRef<HTMLUListElement>(null);
  const focarApos = useRef<{ i: number; restantes: number; grupo: Element | null; posicaoNoGrupo: number } | null>(null);
  useEffect(() => {
    if (!focarApos.current || ids.length !== focarApos.current.restantes) return;
    const { i, grupo, posicaoNoGrupo } = focarApos.current;
    focarApos.current = null;
    const noGrupo = grupo?.isConnected ? grupo : null;
    alvoAposTirarChip({
      daLista: [...(lista.current?.querySelectorAll<HTMLButtonElement>(SELETOR_TIRAR_CHIP) ?? [])],
      i,
      campo: document.getElementById(idCampo),
      doGrupo: [...(noGrupo?.querySelectorAll<HTMLButtonElement>(SELETOR_TIRAR_CHIP) ?? [])],
      posicaoNoGrupo,
      limpar: noGrupo?.querySelector<HTMLButtonElement>(SELETOR_LIMPAR_FILTROS) ?? null,
      busca: document.querySelector<HTMLInputElement>(SELETOR_BUSCA_TABELA),
    })?.focus();
  }, [ids, idCampo]);
  function tirar(alvo: string, i: number, botao: HTMLButtonElement) {
    const restantes = ids.filter((x) => x !== alvo);
    // O grupo de filtros da tela (a seção "Filtrar"), que segue montado quando esta lista some.
    const grupo = lista.current?.closest('section') ?? null;
    const posicaoNoGrupo = grupo ? [...grupo.querySelectorAll(SELETOR_TIRAR_CHIP)].indexOf(botao) : -1;
    focarApos.current = { i, restantes: restantes.length, grupo, posicaoNoGrupo };
    aoMudar(restantes);
  }
  if (!ids.length) return null;
  return (
    <div className={emLinha ? 'flex max-w-full min-w-0 flex-col gap-1' : 'col-span-2 flex w-full min-w-0 flex-col gap-1'}>
      <ul ref={lista} id={idLista} className="flex min-w-0 flex-wrap gap-2" aria-label={rotulo} {...dados}>
        {ids.map((x, i) => (
          <li key={x} className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-lg border border-borda bg-sup-2 py-0.5 pr-0.5 pl-3 text-sm">
            <span className="truncate" title={nomeDe(x)}>
              {nomeDe(x)}
            </span>
            <button type="button" aria-label={t('filtrosLista.remover', { nome: nomeDe(x) })} onClick={(e) => tirar(x, i, e.currentTarget)} data-tirar-chip="" className="flex size-11 shrink-0 items-center justify-center rounded text-tinta-3 hover:text-perigo md:size-7">
              <X className="size-3.5" />
            </button>
          </li>
        ))}
      </ul>
      {cheio && <p className="text-xs text-tinta-3">{t('filtrosLista.limite', { max: MAX_PARCEIROS_FILTRO })}</p>}
    </div>
  );
}

/** Review r25 spec (B1): opções que ainda podem entrar no filtro de vários (as escolhidas saem da lista). */
export function opcoesRestantes<T extends { valor: string }>(opcoes: T[], ids: readonly string[]): T[] {
  const escolhidos = new Set(ids);
  return opcoes.filter((o) => !escolhidos.has(o.valor));
}

/**
 * Review r27 spec (B2): `Selecao` de um valor só (categoria e centro das Movimentações) com um id que não está nas opções
 * (cadastro apagado, link de outro workspace, centro além da primeira página): a `Selecao` mostrava "Todas" com a lista
 * filtrada (0 linhas). O valor vira uma opção a mais com o texto de sem acesso; enquanto as opções carregam, "...".
 */
export function opcoesComEscolhido(opcoes: { valor: string; rotulo: string }[], valor: string, rotulo: string): { valor: string; rotulo: string }[] {
  return valor && !opcoes.some((o) => o.valor === valor) ? [...opcoes, { valor, rotulo }] : opcoes;
}

/**
 * Review r25 spec (B1): filtro de vários com lista fixa (categorias, centros de custo, contas, tipos), como o antigo. A
 * `Selecao` do núcleo acrescenta (volta ao vazio depois de escolher) e os escolhidos ficam nos chips de `ChipsFiltro`,
 * os mesmos de clientes e fornecedores. Sem nenhum escolhido, a `Selecao` mostra o texto de "todos" da tela; com algum,
 * "Adicionar <campo>" (o rótulo do campo, que fica à vista). Na URL, os ids separados por vírgula (um só continua valendo: links e visões de antes).
 * Review r26 spec (B1): `semAcesso` (sem a permissão que lista as opções) não monta a `Selecao`, mas mantém os chips da URL
 * ou da visão salva, cada um como "Cadastro sem acesso" com o botão de tirar (como `FiltroParceiros` sem `cadastros.ver`):
 * a lista nunca fica filtrada sem nada à vista.
 */
export function FiltroVarios({ rotulo, todos, opcoes, ids, aoMudar, semAcesso = false, parte, ...dados }: { rotulo: string; todos: string; opcoes: { valor: string; rotulo: string }[]; ids: string[]; aoMudar: (ids: string[]) => void; semAcesso?: boolean; parte?: ParteFiltro; [dado: `data-${string}`]: string }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const nomes = useMemo(() => new Map(opcoes.map((o) => [o.valor, o.rotulo])), [opcoes]);
  const cheio = ids.length >= MAX_PARCEIROS_FILTRO;
  // Id que não está nas opções (inativo apagado, sem acesso, URL editada): o chip diz que não está acessível.
  // Review r27 spec (O1): com o campo na frente ("Categoria: Cadastro sem acesso"), então os chips de categoria e de centro
  // (e os botões de tirar) não ficam iguais para quem não tem `financeiro.ver`.
  const semAcessoDoCampo = t('filtrosLista.semAcessoCampo', { campo: rotulo });
  const nomeDe = (x: string) => (semAcesso ? semAcessoDoCampo : (nomes.get(x) ?? (opcoes.length ? semAcessoDoCampo : '...')));
  // Review r27 spec (O5): com todas as opções escolhidas, a `Selecao` sai (ficava à vista só com "Adicionar origem", sem
  // nenhuma opção). O foco, que estava nela, vai para o último chip quando a lista nova chega.
  const restantes = opcoesRestantes(opcoes, ids);
  const esgotado = opcoes.length > 0 && restantes.length === 0;
  const focarChip = useRef(false);
  const idChips = useId();
  useEffect(() => {
    if (!focarChip.current || !esgotado) return;
    focarChip.current = false;
    const botoes = document.getElementById(idChips)?.querySelectorAll<HTMLButtonElement>('button');
    botoes?.[botoes.length - 1]?.focus();
  }, [esgotado, idChips]);
  const classeCampo = useClasseCampo('sm:w-56');
  return (
    <>
      {!cheio && !semAcesso && !esgotado && parte !== 'chips' && (
        <div className={classeCampo} {...dados}>
          <Selecao
            id={id}
            aria-label={rotulo}
            vazio={ids.length ? t('filtrosLista.adicionar', { campo: rotulo.toLocaleLowerCase(i18n.language) }) : todos}
            opcoes={restantes}
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (!v || ids.includes(v)) return;
              focarChip.current = restantes.length === 1;
              aoMudar([...ids, v]);
            }}
          />
        </div>
      )}
      {parte !== 'campo' && <ChipsFiltro ids={ids} nomeDe={nomeDe} rotulo={t('filtrosLista.noFiltro', { campo: rotulo })} idCampo={id} idLista={idChips} cheio={cheio} emLinha={parte === 'chips'} aoMudar={aoMudar} data-chips-filtro="" />}
    </>
  );
}

/** Centavos da URL (texto de dígitos) ou null. */
export function centavosDaUrl(v: string | null | undefined): number | null {
  return v && /^\d{1,15}$/.test(v) ? Number(v) : null;
}

/** Faixa válida: valores inteiros não negativos e mínimo menor ou igual ao máximo (os dois são opcionais). */
export function faixaValida(min: number | null, max: number | null): boolean {
  if (min !== null && (!Number.isSafeInteger(min) || min < 0)) return false;
  if (max !== null && (!Number.isSafeInteger(max) || max < 0)) return false;
  return min === null || max === null || min <= max;
}

/**
 * Valor mínimo e máximo, digitados como dinheiro no idioma (máscara do núcleo, modo discreto) e levados à URL em centavos
 * inteiros (`valor_min_centavos`, `valor_max_centavos`), com 300 ms de espera (como a busca da Tabela). Faixa invertida ou
 * valor negativo não vai à URL: o campo do máximo fica inválido com a mensagem embaixo.
 */
export function FiltroValor({ min, max, aoMudar }: { min: string; max: string; aoMudar: (v: { valor_min_centavos: string; valor_max_centavos: string }) => void }) {
  const { t } = useTranslation();
  const idMin = useId();
  const idMax = useId();
  const idErro = useId();
  const [vMin, setVMin] = useState<number | null>(() => centavosDaUrl(min));
  const [vMax, setVMax] = useState<number | null>(() => centavosDaUrl(max));
  // A URL mudou por fora (Limpar filtros, visão salva, Voltar): os campos acompanham.
  useEffect(() => setVMin(centavosDaUrl(min)), [min]);
  useEffect(() => setVMax(centavosDaUrl(max)), [max]);
  const valida = faixaValida(vMin, vMax);
  const minRuim = vMin !== null && vMin < 0;
  const maxRuim = !valida && !minRuim;
  // Review r37 visual (M3): no painel, mínimo e máximo ocupam a coluna (na faixa, `sm:w-36`).
  const largura = useContext(NoPainelFiltros) ? '' : ' sm:w-36';
  const erro = valida ? null : minRuim || (vMax !== null && vMax < 0) ? t('erros.valor') : t('filtrosLista.faixaInvalida');
  const pendente = useDebounce(JSON.stringify([vMin, vMax]));
  useEffect(() => {
    const [a, b] = JSON.parse(pendente) as [number | null, number | null];
    if (!faixaValida(a, b)) return;
    const novo = { valor_min_centavos: a === null ? '' : String(a), valor_max_centavos: b === null ? '' : String(b) };
    if (novo.valor_min_centavos !== min || novo.valor_max_centavos !== max) aoMudar(novo);
  }, [pendente]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className={`flex min-w-0 flex-col gap-1${largura}`} data-filtro-valor-min="">
        <label htmlFor={idMin} className="text-xs font-medium text-tinta-3">
          {t('filtrosLista.valorMin')}
        </label>
        <EntradaDinheiro id={idMin} valor={vMin} aoMudar={setVMin} aria-invalid={minRuim} aria-describedby={minRuim ? idErro : undefined} />
      </div>
      <div className={`flex min-w-0 flex-col gap-1${largura}`} data-filtro-valor-max="">
        <label htmlFor={idMax} className="text-xs font-medium text-tinta-3">
          {t('filtrosLista.valorMax')}
        </label>
        <EntradaDinheiro id={idMax} valor={vMax} aoMudar={setVMax} aria-invalid={maxRuim} aria-describedby={maxRuim ? idErro : undefined} />
      </div>
      {erro && (
        <p id={idErro} role="alert" className="col-span-2 w-full text-xs text-perigo">
          {erro}
        </p>
      )}
    </>
  );
}

/**
 * Review r37 visual (M3): dentro do painel "Mais filtros" (grade de 2 colunas num painel de `w-80 sm:w-96`) os campos
 * ocupam a coluna, sem a largura fixa da faixa (`sm:w-56`, `sm:w-64`), que passava da coluna: os campos se sobrepunham,
 * saíam pela borda do painel e, em Movimentações a 1280, empurravam a página (rolagem horizontal).
 */
export const NoPainelFiltros = createContext(false);
/** Classe do invólucro de um campo de filtro: na faixa, a largura fixa dele; no painel, a coluna inteira. */
function useClasseCampo(larguraNaFaixa: string): string {
  return useContext(NoPainelFiltros) ? 'col-span-2 min-w-0 sm:col-span-1' : `col-span-2 sm:col-span-1 ${larguraNaFaixa}`;
}

/**
 * C1 (A receber e A pagar; C2, Movimentações): "Mais filtros" em painel, como no antigo. O botão mostra quantos filtros do
 * painel estão em uso ("Mais filtros (3)"); o painel é o `Popover` do núcleo (não modal: Esc, clique fora e Tab fecham) e os
 * escolhidos ficam à vista na linha de chips embaixo da faixa (`LinhaChips`).
 */
export function MaisFiltros({ contagem, children }: { contagem: number; children: ReactNode }) {
  const { t } = useTranslation();
  const texto = contagem ? t('filtrosLista.maisFiltrosN', { n: contagem }) : t('filtrosLista.maisFiltros');
  return (
    <Popover rotulo={t('filtrosLista.maisFiltros')} texto={texto} icone={<SlidersHorizontal className="size-4" aria-hidden />} classePainel="left-0 w-80 sm:w-96" data-mais-filtros="">
      <div className="grid grid-cols-2 items-end gap-2 p-1" data-painel-filtros="">
        <NoPainelFiltros.Provider value>{children}</NoPainelFiltros.Provider>
      </div>
    </Popover>
  );
}

/** C1: quantos filtros do painel estão em uso (um por campo, como o antigo: "Mais filtros (6)"). */
export function contarFiltros(valores: readonly unknown[]): number {
  return valores.filter((v) => (Array.isArray(v) ? v.length > 0 : !!v)).length;
}

/**
 * C1: linha dos filtros aplicados, embaixo da faixa: os chips (cada um com o "x") e "Limpar filtros" no fim, como no antigo.
 * Sem filtro nenhum, não aparece.
 */
export function LinhaChips({ visivel, rotuloLimpar, aoLimpar, children }: { visivel: boolean; rotuloLimpar: string; aoLimpar: () => void; children: ReactNode }) {
  if (!visivel) return null;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2" data-linha-chips="">
      {children}
      <Botao tamanho="sm" variante="fantasma" className="whitespace-nowrap" icone={<FilterX className="size-4" />} onClick={() => limparFiltrosEFocarBusca(aoLimpar)} data-limpar-filtros="">
        {rotuloLimpar}
      </Botao>
    </div>
  );
}

/** C1: chips de valores fixos (estado, faixa de valor) na linha de chips, com o mesmo chip e o mesmo foco dos outros. */
export function ChipsFixos({ itens, rotulo, aoTirar, ...dados }: { itens: { chave: string; rotulo: string }[]; rotulo: string; aoTirar: (chave: string) => void; [dado: `data-${string}`]: string }) {
  const id = useId();
  const nomes = new Map(itens.map((i) => [i.chave, i.rotulo]));
  const ids = itens.map((i) => i.chave);
  return <ChipsFiltro ids={ids} nomeDe={(x) => nomes.get(x) ?? x} rotulo={rotulo} idCampo={id} cheio={false} emLinha aoMudar={(restantes) => ids.filter((x) => !restantes.includes(x)).forEach(aoTirar)} {...dados} />;
}

/** C1: chips da faixa de valor ("Valor mínimo: R$ 10,00"), que tiram o mínimo ou o máximo da URL. */
export function ChipsValor({ min, max, aoMudar }: { min: string; max: string; aoMudar: (v: Record<string, string>) => void }) {
  const { t } = useTranslation();
  useDiscreto();
  const moeda = useSessao().workspace.moeda;
  const faixa = faixaDaUrl(min, max);
  const itens = [
    ...(faixa.valor_min_centavos ? [{ chave: 'valor_min_centavos', rotulo: t('filtrosLista.chipValor', { campo: t('filtrosLista.valorMin'), valor: dinheiro(Number(faixa.valor_min_centavos), moeda) }) }] : []),
    ...(faixa.valor_max_centavos ? [{ chave: 'valor_max_centavos', rotulo: t('filtrosLista.chipValor', { campo: t('filtrosLista.valorMax'), valor: dinheiro(Number(faixa.valor_max_centavos), moeda) }) }] : []),
  ];
  return <ChipsFixos itens={itens} rotulo={rotuloChipsValor(t)} aoTirar={(k) => aoMudar({ [k]: '' })} data-chips-valor="" />;
}

/** Review r40 visual (B1): o nome da lista vale para o mínimo, o máximo ou os dois (antes dizia "Valor mínimo" sempre). */
export const rotuloChipsValor = (t: (chave: string, op?: Record<string, unknown>) => string) => t('filtrosLista.noFiltro', { campo: t('filtrosLista.faixaValor') });

/**
 * C1: período com a base à vista, como no antigo: alternador `Segmentado` [Vencimento | Competência] e o `SeletorPeriodo` do
 * núcleo, com "Todo o período" no topo dos atalhos (o padrão da tela, sem datas) e os atalhos de sempre (Hoje, Esta semana,
 * Mês atual...). A base fica na URL (`campo_data`, a primeira base não vai) mesmo sem datas.
 */
export function PeriodoComBase({ bases, campo, de: deUrl, ate: ateUrl, aoMudar }: { bases: readonly BasePeriodo[]; campo: string; de: string; ate: string; aoMudar: (v: { campo_data: string; de: string; ate: string }) => void }) {
  const { t } = useTranslation();
  const s = useSessao();
  const hoje = hojeNoFuso(s.workspace.fuso);
  const { de, ate } = periodoDaUrl(deUrl, ateUrl);
  const base: BasePeriodo = (bases as readonly string[]).includes(campo) ? (campo as BasePeriodo) : bases[0]!;
  const atual = periodoValido({ de, ate }) ? { de, ate } : null;
  const periodo = atual ?? periodoDoAtalho('mes', hoje);
  const campoDa = (b: BasePeriodo) => (b === bases[0] ? '' : b);
  return (
    <>
      <Segmentado
        rotulo={t('filtrosLista.periodoPor')}
        valor={base}
        opcoes={bases.map((b) => ({ valor: b, rotulo: t(`filtrosLista.bases.${b}`) }))}
        aoMudar={(b) => aoMudar({ campo_data: campoDa(b), de: atual?.de ?? '', ate: atual?.ate ?? '' })}
        data-filtro-base=""
      />
      <div data-filtro-periodo="">
        <SeletorPeriodo
          periodo={periodo}
          escolha={escolhaDoPeriodo(periodo, hoje)}
          todo={{ ativo: !atual, aoEscolher: () => aoMudar({ campo_data: campoDa(base), de: '', ate: '' }) }}
          aoEscolher={(e: PeriodoGuardado) => {
            const p = e.atalho === 'personalizado' ? { de: e.de, ate: e.ate } : periodoDoAtalho(e.atalho, hoje);
            aoMudar({ campo_data: campoDa(base), de: p.de, ate: p.ate });
          }}
        />
      </div>
    </>
  );
}
